import { Box, Button, Input, useToast } from "@chakra-ui/react";
import { Gender, MAX_NAME_LENGTH, MIN_NAME_LENGTH } from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { AppPage } from "../utils/AppPage";
import { GenderSelector } from "../utils/GenderSelector";
import { TeamSelector } from "../utils/TeamSelector";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { isParamNumeric } from "../utils/utils";
import { YearSelector } from "../utils/YearSelector";
import { useGetYouthKidById } from "./useGetYouthKidById";
import { PropertyForm } from "./PropertyForm";

export const YouthKidEditPage = () => {
  const { youthKidId } = useParams();
  const youthKid = useGetYouthKidById(youthKidId);
  const [fName, setFName] = useState("");
  const [lName, setLName] = useState("");
  const [year, setYear] = useState<null | number>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [teamId, setTeamId] = useState("");
  const [properties, setProperties] = useState<{ [name: string]: string }>({});
  const toast = useToast();

  const api = useAPI();
  const { data, state, error, query } = useAPIQuery<"youthKidPut">({
    apiCall: api.youthKidPut,
  });
  const navigate = useNavigate();
  useToastOnAPIError({ title: "Unable to edit youth kid", error });
  useOnAPISuccess<"youthKidPut">({
    data,
    state,
    onSuccess: () => {
      if (youthKid) {
        toast({
          position: "top",
          status: "success",
          title: `Successfully updated ${youthKid.fName} ${youthKid.lName}`,
        });
        navigate(`/youth-kid/${youthKid.id}`);
      }
    },
  });

  // Set form initial value
  useEffect(() => {
    if (youthKid) {
      setFName(youthKid.fName);
      setLName(youthKid.lName);
      setYear(youthKid.year);
      setGender(youthKid.gender);
      setTeamId(youthKid.teamId ? `${youthKid.teamId}` : ``);
      setProperties(youthKid.properties);
    }
  }, [youthKid]);

  const changes = useMemo(
    () =>
      youthKid
        ? {
            ...(fName !== youthKid.fName ? { fName } : {}),
            ...(lName !== youthKid.lName ? { lName } : {}),
            ...(year !== youthKid.year && year ? { year } : {}),
            ...(gender !== youthKid.gender && gender ? { gender } : {}),
            ...(isParamNumeric(teamId) && youthKid.teamId !== Number(teamId)
              ? { teamId: Number(teamId) }
              : {}),
          }
        : {},
    [fName, gender, lName, teamId, year, youthKid]
  );

  const isValid = useMemo(
    () =>
      fName.length >= MIN_NAME_LENGTH &&
      lName.length <= MAX_NAME_LENGTH &&
      lName.length >= MIN_NAME_LENGTH &&
      lName.length <= MAX_NAME_LENGTH &&
      year &&
      gender,
    [fName.length, gender, lName.length, year]
  );

  const onSave = useCallback(() => {
    if (!year || !gender || state === "loading" || !youthKid) {
      return;
    }
    query({
      id: youthKid.id,
      ...changes,
      properties,
    });
  }, [changes, gender, properties, query, state, year, youthKid]);

  return (
    <AppPage title="Edit youth kid">
      <Box fontWeight={"bold"} fontSize={"large"} marginTop={2}>
        First Name
      </Box>
      <Input
        placeholder="First Name"
        marginBottom={4}
        value={fName}
        onChange={(e) => setFName(e.target.value)}
      />
      <Box fontWeight={"bold"} fontSize={"large"} marginTop={2}>
        Last Name
      </Box>
      <Input
        placeholder="Last Name"
        marginBottom={4}
        value={lName}
        onChange={(e) => setLName(e.target.value)}
      />
      <Box fontWeight={"bold"} fontSize={"large"} marginTop={2}>
        Gender
      </Box>
      <GenderSelector gender={gender} setGender={setGender} />
      <Box fontWeight={"bold"} fontSize={"large"} marginTop={2}>
        Year
      </Box>
      <YearSelector year={year} setYear={setYear} />
      <TeamSelector value={teamId} setValue={setTeamId} />
      <PropertyForm properties={properties} setProperties={setProperties} />
      <Button
        marginTop={8}
        width={"100%"}
        colorScheme={THEME_COLOR_SCHEME}
        isDisabled={!isValid}
        onClick={onSave}
      >
        Save
      </Button>
    </AppPage>
  );
};
